/**
 * Drop-in street-address input with a Mapbox-backed suggestions dropdown.
 *
 * Unlike `AddressAutocomplete` (which owns the whole broken-out address block
 * and collapses/reveals), this is a *single-field* replacement: it renders the
 * exact `<input>` the host already had — forwarding id / data-testid / value /
 * disabled / placeholder / className untouched — wrapped in a `relative` div
 * that also floats a suggestions list. The host keeps its own always-visible
 * city / state / zip inputs; picking a suggestion just autofills them via
 * `onPick`.
 *
 * Contract with the host:
 *   - `onChange(raw)` fires on every keystroke (same as a plain input's
 *     onChange forwarding `e.target.value`), so existing controlled state and
 *     tests keep working verbatim.
 *   - `onPick(parsed)` fires once when a suggestion is chosen, handing back the
 *     parsed `{ streetAddress, city, state, zip, … }` for the host to spread
 *     onto its sibling fields. The picked street is ALSO sent through
 *     `onChange` so the street field stays in sync.
 */
import { useEffect, useState } from 'react';
import { MapPin } from 'lucide-react';
import { useAddressAutocomplete } from '@/hooks/useAddressAutocomplete';
import { parseMapboxAddress, type ParsedAddress } from '@/lib/mapbox/geocoding';
import { cn } from '@/lib/utils';

// Mirrors the hook's own floor: below this the geocoder isn't queried, so
// there's nothing to say about matches yet.
const MIN_QUERY_LENGTH = 3;

export interface AddressSuggestInputProps {
  id?: string;
  'data-testid'?: string;
  value: string;
  /** Called on every keystroke — forwarded from the input's `e.target.value`. */
  onChange: (value: string) => void;
  /** Called when a suggestion is chosen, with the parsed address components. */
  onPick: (parsed: ParsedAddress) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  /** Location bias passed through to the geocoder. */
  proximity?: { lng: number; lat: number };
  /** Gate the token fetch (default true). Disabled always gates it off. */
  enabled?: boolean;
}

export function AddressSuggestInput({
  id,
  'data-testid': testId,
  value,
  onChange,
  onPick,
  placeholder,
  disabled = false,
  className,
  proximity,
  enabled = true,
}: AddressSuggestInputProps) {
  const { setQuery, suggestions, loading } = useAddressAutocomplete({
    enabled: enabled && !disabled,
    proximity,
  });

  // `open` drives dropdown visibility. It opens on focus / typing and closes on
  // blur (covers outside-click), Escape, and picking — the manual path is just
  // to ignore the dropdown and keep typing into the always-visible fields.
  const [open, setOpen] = useState(false);

  // Keep the hook's query aligned with the externally-controlled value so a
  // host-driven change (e.g. a prefilled address) tracks the suggestions fetch.
  useEffect(() => {
    setQuery(value);
  }, [value, setQuery]);

  const handleChange = (next: string) => {
    onChange(next);
    setQuery(next);
    setOpen(true);
  };

  const handlePick = (feature: (typeof suggestions)[number]) => {
    const parsed = parseMapboxAddress(feature);
    onChange(parsed.streetAddress);
    onPick(parsed);
    setOpen(false);
  };

  const showDropdown = open && suggestions.length > 0;
  // When the user has typed enough but Mapbox returned nothing (a bare house
  // number, an unrecognised street), show a hint rather than a dead field —
  // and not while a search is still debouncing/in flight.
  const showEmptyHint =
    open && !loading && suggestions.length === 0 && value.trim().length >= MIN_QUERY_LENGTH;

  return (
    <div className="relative w-full">
      <input
        id={id}
        data-testid={testId}
        type="text"
        value={value}
        onChange={(e) => handleChange(e.target.value)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setOpen(false);
        }}
        disabled={disabled}
        placeholder={placeholder}
        // w-full because the wrapper div (unlike the flex-col label the input
        // used to sit directly in) doesn't stretch its children.
        className={cn('w-full', className)}
        autoComplete="off"
        // Tell password managers to skip this field — otherwise 1Password /
        // LastPass float their own autofill menu over our suggestions dropdown
        // and swallow the click. (autoComplete="off" alone doesn't stop them.)
        data-1p-ignore
        data-lpignore="true"
      />

      {showDropdown && (
        <ul
          role="listbox"
          className="absolute z-50 mt-1 w-full overflow-hidden rounded-md border bg-popover shadow-md"
        >
          {suggestions.map((feature) => {
            const parsed = parseMapboxAddress(feature);
            return (
              <li key={feature.id}>
                <button
                  type="button"
                  // Prevent the input's blur from firing before the click so
                  // the pick isn't swallowed by the dropdown closing on blur.
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => handlePick(feature)}
                  className="flex w-full items-start gap-2 px-2 py-2 text-left hover:bg-accent"
                >
                  <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-semibold">
                      {parsed.streetAddress || feature.text}
                    </span>
                    <span className="block truncate text-xs text-muted-foreground">
                      {feature.place_name}
                    </span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {showEmptyHint && (
        <div className="absolute z-50 mt-1 w-full rounded-md border bg-popover px-2 py-2 text-xs text-muted-foreground shadow-md">
          No matches — keep typing the street name, or just fill the fields below.
        </div>
      )}
    </div>
  );
}

export default AddressSuggestInput;
